import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import {
    cancelPurchaseDebitNote,
    deletePurchaseDebitNote,
    getPurchaseDebitNoteDetails,
} from '../../services/PurchaseDebitNoteServices';
import { fetchAndShowPurchaseDebitNotePdf } from '../../utils/purchaseDebitNotePdf';
import {
    buildPurchaseDebitNoteTotals,
    getPurchaseDebitNoteTypeLabel,
} from './PurchaseDebitNoteUtils';
import TableRowsPurchaseDebitNote from './TableRowsPurchaseDebitNote';
import moment from 'moment';

function PurchaseDebitNoteEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [documentLines, setDocumentLines] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const isCancelled = inputs.status === false || inputs.status === 0 || inputs.status === 'Cancelled';

    const totals = useMemo(
        () => buildPurchaseDebitNoteTotals(productLines, documentLines),
        [productLines, documentLines],
    );

    useEffect(() => {
        setInProgress(true);
        getPurchaseDebitNoteDetails(cookies, id)
            .then((response) => {
                setInputs({
                    ...response,
                    dn_date: moment(response.dn_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                    dn_no: response.dn_no,
                });
                setProductLines(response.product_lines || []);
                setDocumentLines((response.document_lines || []).map((line) => ({
                    ...line,
                    doc_type: 'GRN',
                    reference_no: response.reference_no,
                    debit_amt: line.debit_amt,
                })));
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load purchase debit note.' }, 'Load Failed');
            })
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const onPrint = () => {
        setInProgress(true);
        fetchAndShowPurchaseDebitNotePdf(cookies, id, { title: 'Purchase Debit Note PDF' })
            .finally(() => setInProgress(false));
    };

    const onCancel = () => {
        Swal.fire({
            title: 'Cancel this purchase debit note?',
            text: 'Mark as inactive? Values are retained for audit.',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelPurchaseDebitNote(cookies, id)
                .then((response) => {
                    Swal.fire(response.message || 'Cancelled!', '', 'success');
                    navigate('/PurchaseDebitNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Cancel Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const onDelete = () => {
        Swal.fire({
            title: 'Delete this cancelled purchase debit note?',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deletePurchaseDebitNote(cookies, id)
                .then(() => {
                    Swal.fire('Deleted!', '', 'success');
                    navigate('/PurchaseDebitNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Delete Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form>
                    <FloatingControls tableLink="/PurchaseDebitNoteTable" formLink="/PurchaseDebitNote" addNewLabel="Add New" enableCancel={false} />
                    <FormPageLayout
                        moduleLabel="PURCHASE"
                        title="Update Purchase Debit Note"
                        subtitle="Review vendor debit note against GRN for rate difference or additional charges."
                        modeLabel={`${getPurchaseDebitNoteTypeLabel(inputs.dn_type).toUpperCase()} UPDATE MODE`}
                        headerExtra={isCancelled ? (
                            <div className="alert alert-warning mb-0">This purchase debit note is inactive. Values are retained for audit only.</div>
                        ) : null}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-5 text-right">Plant</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.plant_alias || inputs.plant_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Vendor</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.vendor_name || inputs.vendor_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">GRN Reference</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.reference_no || inputs.gmr_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Debit Note Type</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.dn_type_label || getPurchaseDebitNoteTypeLabel(inputs.dn_type)} /><br />
                                        <label className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.voucher_mode || ''} />
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Debit Note No.</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.dn_no || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Debit Note Date</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.dn_date || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Status</label>
                                        <input className="form-control col-sm-6" readOnly value={isCancelled ? 'Cancelled' : 'Active'} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsPurchaseDebitNote rowsData={productLines} taxes={[]} dnType={inputs.dn_type} readOnly />
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Reference No</th>
                                            <th>Debit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documentLines.map((line, index) => (
                                            <tr key={`pdn-edit-doc-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'GRN'}</td>
                                                <td>{line.reference_no || inputs.reference_no}</td>
                                                <td>{line.debit_amt}</td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" className="form-control col-sm-10" readOnly value={inputs.remarks || ''} />
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-3 text-right">Total Debit Note Value</label>
                                <input className="form-control col-sm-3" readOnly value={totals.debit_amt || inputs.debit_amt || 0} />
                            </div>

                            {inputs.accounting_hint && (
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <label className="form-group col-sm-2 text-right">Accounting</label>
                                    <div className="col-sm-10 small text-muted">
                                        <div>{inputs.accounting_hint.party_ledger} · {inputs.accounting_hint.expense_ledger} · {inputs.accounting_hint.gst_ledger}</div>
                                        <div>{inputs.accounting_hint.narration}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="footer text-center">
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={onPrint}>Print</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/PurchaseDebitNoteTable')}>View</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>Home</Button>
                            {!isCancelled && (
                                <Button type="button" className="btn btn-cancel" style={{ width: '120px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onCancel}>Cancel PDN</Button>
                            )}
                            {isCancelled && (
                                <Button type="button" className="btn btn-delete" style={{ width: '80px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onDelete}>Delete</Button>
                            )}
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default PurchaseDebitNoteEdit;
